import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { openVipModal } from "@/lib/vipModal";
import { optimizeImage } from "@/lib/img";

/**
 * Top-level nav schema with dropdowns.
 * `to` on the top-level item is optional — when omitted, clicking the label only opens the menu.
 */
const NAV = [
  {
    label: "Membership",
    to: "/#membership",
    children: [
      { label: "Activate", to: "/#membership" },
      { label: "Accelerate", to: "/#membership" },
    ],
  },
  {
    label: "Community",
    children: [
      { label: "Events", to: "/#events" },
      { label: "Member Directory", to: "/her-guide" },
      { label: "Community Guidelines", to: "/guidelines" },
    ],
  },
  {
    label: "Resources",
    children: [
      { label: "Journal", to: "/blog" },
      { label: "Templates", to: "/#resources" },
      { label: "Marketing Guides", to: "/blog" },
    ],
  },
  {
    label: "About",
    to: "/about",
    children: [
      { label: "Our Story", to: "/about" },
      { label: "Founder", to: "/about" },
      { label: "FAQ", to: "/about" },
    ],
  },
];

function isExternalHash(to) {
  return typeof to === "string" && to.startsWith("/#");
}

function NavLinkOrAnchor({ to, children, onClick, className, style, dataTestId }) {
  // For both regular paths and hash links (/#membership), use React Router <Link>.
  // ScrollToTop handles the hash jump once the destination route mounts.
  return (
    <Link
      to={to}
      className={className}
      style={style}
      onClick={onClick}
      data-testid={dataTestId}
    >
      {children}
    </Link>
  );
}
// keep the import alive in case future logic needs it
void isExternalHash;

function NavDropdown({ item, linkColor, linkHover }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const closeTimer = useRef(null);

  const openMenu = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  };

  useEffect(() => {
    return () => clearTimeout(closeTimer.current);
  }, []);

  return (
    <div
      ref={wrap}
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
      onFocus={openMenu}
      onBlur={(e) => {
        if (!wrap.current?.contains(e.relatedTarget)) scheduleClose();
      }}
    >
      <NavLinkOrAnchor
        to={item.to || "#"}
        className="flex items-center gap-1 text-[0.72rem] font-semibold tracking-wider uppercase transition-colors duration-300"
        style={{ color: linkColor }}
        onClick={(e) => {
          if (!item.to) e.preventDefault();
        }}
        dataTestId={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <span
          onMouseEnter={(e) => (e.currentTarget.style.color = linkHover)}
          onMouseLeave={(e) => (e.currentTarget.style.color = linkColor)}
          style={{ color: "inherit" }}
        >
          {item.label}
        </span>
        <ChevronDown
          size={12}
          aria-hidden="true"
          className="transition-transform duration-200"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </NavLinkOrAnchor>

      {/* Dropdown */}
      <div
        className={`absolute left-0 top-full pt-3 min-w-[220px] transition-all duration-150 ${
          open ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        role="menu"
      >
        <div
          className="rounded-xl py-2 shadow-2xl"
          style={{
            background: "rgba(31, 26, 21, 0.97)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            border: "1px solid rgba(234, 216, 199, 0.12)",
          }}
        >
          {item.children.map((c) => (
            <NavLinkOrAnchor
              key={c.label}
              to={c.to}
              className="block px-5 py-2.5 text-[0.78rem] font-medium tracking-wide transition-colors"
              style={{ color: "var(--lh-warm-ivory)" }}
              onClick={() => setOpen(false)}
              dataTestId={`nav-child-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--lh-peach)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--lh-warm-ivory)")}
                style={{ color: "inherit" }}
              >
                {c.label}
              </span>
            </NavLinkOrAnchor>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MarketingHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  const isLanding = location.pathname === "/";
  const lightMode = isLanding && !scrolled && !open;

  useEffect(() => {
    if (!isLanding) return;
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isLanding]);

  const linkColor = "var(--lh-cream)";
  const linkHover = "var(--lh-peach)";
  const headerStyle = lightMode
    ? { background: "transparent" }
    : {
        background: "rgba(31, 26, 21, 0.96)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(234, 216, 199, 0.08)",
      };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={headerStyle}
      data-testid="marketing-header"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-4 sm:px-6 md:px-10 lg:px-14 py-4 lg:py-5">
        <Link to="/" data-testid="header-home-link" className="flex items-center relative">
          <img
            src={optimizeImage("https://customer-assets.emergentagent.com/job_her-brand-builders/artifacts/4eqxk9v7_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2011_47_17%20PM.png", { w: 320, q: 88, name: "localizeher-logo" })}
            alt="LocalizeHER"
            className="h-9 sm:h-10 lg:h-14 w-auto object-contain"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            style={{
              mixBlendMode: "screen",
              transform: "scale(1.6)",
              transformOrigin: "left center",
            }}
            data-testid="brand-logo"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <NavDropdown key={n.label} item={n} linkColor={linkColor} linkHover={linkHover} />
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          {user && user !== false ? (
            <Link
              to="/dashboard"
              className="btn-mauve"
              data-testid="header-dashboard-button"
            >
              Dashboard <span aria-hidden>→</span>
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => openVipModal("interested", "header")}
              className="btn-mauve"
              data-testid="header-vip-button"
            >
              Reserve Your VIP Spot <span aria-hidden>→</span>
            </button>
          )}
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          style={{ color: linkColor }}
          data-testid="header-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden px-6 py-8 max-h-[calc(100vh-72px)] overflow-y-auto"
          style={{
            background: "rgba(31, 26, 21, 0.98)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            borderTop: "1px solid rgba(234, 216, 199, 0.08)",
          }}
          data-testid="header-mobile-menu"
        >
          <nav className="space-y-5">
            {NAV.map((n) => (
              <div key={n.label}>
                <NavLinkOrAnchor
                  to={n.to || "#"}
                  className="block text-sm font-bold tracking-widest uppercase mb-3"
                  style={{ color: "var(--lh-peach)" }}
                  onClick={(e) => {
                    if (!n.to) e.preventDefault();
                    else setOpen(false);
                  }}
                >
                  {n.label}
                </NavLinkOrAnchor>
                <div className="pl-4 space-y-3 border-l border-[var(--lh-peach)]/30">
                  {n.children.map((c) => (
                    <NavLinkOrAnchor
                      key={c.label}
                      to={c.to}
                      className="block text-[0.95rem] font-medium tracking-wide"
                      style={{ color: "var(--lh-warm-ivory)" }}
                      onClick={() => setOpen(false)}
                    >
                      {c.label}
                    </NavLinkOrAnchor>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          <div className="mt-8 pt-6 flex flex-col gap-3 border-t border-[var(--lh-warm-ivory)]/10">
            {user && user !== false ? (
              <Link
                to="/dashboard"
                className="btn-mauve justify-center w-full"
                onClick={() => setOpen(false)}
              >
                Dashboard <span aria-hidden>→</span>
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-1.5 w-full px-4 py-3 rounded-full text-sm font-bold tracking-widest uppercase transition-colors"
                  style={{
                    color: "var(--lh-peach)",
                    border: "1.5px solid var(--lh-peach)",
                    background: "transparent",
                  }}
                  onClick={() => setOpen(false)}
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="btn-mauve justify-center w-full"
                  onClick={() => setOpen(false)}
                >
                  Join Now <span aria-hidden>→</span>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
